import { TrendingUp, TrendingDown } from 'lucide-react';
import type { CompanyFinancialSummary, IndustryBenchmarkRecord, RegionConfig } from '../../types/industryResearch';
import { formatCurrencyValue } from '../financial/utils/formatters';

interface Props {
  companies: CompanyFinancialSummary[];
  medians: IndustryBenchmarkRecord;
  region?: RegionConfig;
}

// ── cell helpers ───────────────────────────────────────────────────────────────

function CurrencyCell({
  value, aboveMedian, region,
}: { value: number | null | undefined; aboveMedian?: boolean | null; region?: RegionConfig }) {
  if (value == null) return <Dash />;
  const Icon = aboveMedian === true ? TrendingUp : aboveMedian === false ? TrendingDown : null;
  const color = aboveMedian === true ? 'text-green-700' : aboveMedian === false ? 'text-red-600' : 'text-neutral-700';
  return (
    <span className={`flex items-center justify-end gap-1 ${color} text-xs font-medium`}>
      {Icon && <Icon className="h-3 w-3 shrink-0" />}
      {formatCurrencyValue(value, region)}
    </span>
  );
}

function PctCell({
  value, aboveMedian, invertColor = false,
}: { value: number | null | undefined; aboveMedian?: boolean | null; invertColor?: boolean }) {
  if (value == null) return <Dash />;
  const isGood = invertColor ? aboveMedian === false : aboveMedian === true;
  const isBad  = invertColor ? aboveMedian === true  : aboveMedian === false;
  const cls = isGood
    ? 'text-green-700 bg-green-50'
    : isBad
      ? 'text-red-600 bg-red-50'
      : 'text-neutral-600 bg-neutral-100';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${cls}`}>
      {value.toFixed(1)}%
    </span>
  );
}

function MultipleCell({
  value, aboveMedian,
}: { value: number | null | undefined; aboveMedian?: boolean | null }) {
  if (value == null) return <Dash />;
  const cls = aboveMedian === true
    ? 'text-green-700 bg-green-50'
    : aboveMedian === false
      ? 'text-red-600 bg-red-50'
      : 'text-neutral-600 bg-neutral-100';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${cls}`}>
      {value.toFixed(1)}x
    </span>
  );
}

function Dash() {
  return <span className="text-neutral-300 text-xs select-none">—</span>;
}

// ── main component ─────────────────────────────────────────────────────────────

export default function IndustryCompetitorFinancialTable({ companies, medians, region }: Props) {
  const unit = region?.currency?.unit_abbr ? ` (${region.currency.unit_abbr})` : '';

  const sorted = [...companies]
    .filter((c) => c.has_financial_data)
    .sort((a, b) => (b.market_share ?? 0) - (a.market_share ?? 0));

  // Pre-compute median display values
  const mRev     = medians.total_revenue;
  const mEbitda  = medians.ebitda;
  const mMargin  = medians.ebitda_margin != null ? medians.ebitda_margin * 100 : null;
  const mNetProf = medians.net_profit;
  const mRoic    = medians.roic != null ? medians.roic * 100 : null;
  const mEvEbitda= medians.ev_ebitda;
  const mEv      = medians.enterprise_value;

  const TH = ({ children, first = false }: { children: React.ReactNode; first?: boolean }) => (
    <th className={`px-3 py-2.5 text-xs font-semibold text-neutral-500 whitespace-nowrap ${first ? 'text-left' : 'text-right'}`}>
      {children}
    </th>
  );

  const MED = ({ children }: { children: React.ReactNode }) => (
    <td className="px-3 py-2.5 text-right text-xs font-semibold text-blue-700 whitespace-nowrap">
      {children}
    </td>
  );

  const TD = ({ children }: { children: React.ReactNode }) => (
    <td className="px-3 py-3 text-right align-middle">
      {children}
    </td>
  );

  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-neutral-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">Competitor Financial Comparison</h3>
        <span className="text-xs text-neutral-400">Latest year · color = vs. industry median</span>
      </div>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-neutral-50 border-b border-neutral-200">
            <TH first>Company</TH>
            <TH>Share</TH>
            <TH>Revenue{unit}</TH>
            <TH>EBITDA{unit}</TH>
            <TH>EBITDA Margin</TH>
            <TH>Net Profit{unit}</TH>
            <TH>ROIC</TH>
            <TH>EV/EBITDA</TH>
            <TH>EV{unit}</TH>
          </tr>
        </thead>
        <tbody>

          {/* ── Industry Median row ── */}
          <tr className="bg-blue-50/60 border-b border-blue-100">
            <td className="px-4 py-2.5">
              <span className="text-xs font-bold text-blue-700">Industry Median</span>
            </td>
            <MED>—</MED>
            <MED>{formatCurrencyValue(mRev, region)}</MED>
            <MED>{formatCurrencyValue(mEbitda, region)}</MED>
            <MED>{mMargin != null ? `${mMargin.toFixed(1)}%` : '—'}</MED>
            <MED>{formatCurrencyValue(mNetProf, region)}</MED>
            <MED>{mRoic != null ? `${mRoic.toFixed(1)}%` : '—'}</MED>
            <MED>{mEvEbitda != null ? `${mEvEbitda.toFixed(1)}x` : '—'}</MED>
            <MED>{formatCurrencyValue(mEv, region)}</MED>
          </tr>

          {/* ── Company rows ── */}
          {sorted.map((co) => (
            <tr
              key={co.company_name}
              className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50/60 transition-colors"
            >
              {/* Company identity */}
              <td className="px-4 py-3 align-middle min-w-[180px]">
                <p className="text-sm font-medium text-neutral-800 leading-snug">{co.company_name}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {co.ticker && (
                    <span className="text-[11px] text-neutral-400 font-mono">{co.ticker}</span>
                  )}
                  {co.latest_year && (
                    <span className="text-[10px] text-neutral-400">FY{co.latest_year}</span>
                  )}
                  {co.years_of_data > 0 && (
                    <span className="text-[10px] text-neutral-400">{co.years_of_data}y</span>
                  )}
                  {!co.has_financial_data && (
                    <span className="text-xs text-amber-600 italic">No data</span>
                  )}
                </div>
              </td>

              <TD>
                <span className="text-xs text-neutral-600">
                  {co.market_share != null ? `${co.market_share.toFixed(1)}%` : '—'}
                </span>
              </TD>

              <TD>
                {co.has_financial_data
                  ? <CurrencyCell value={co.latest_revenue} aboveMedian={co.above_median_revenue} region={region} />
                  : <Dash />}
              </TD>

              <TD>
                {co.has_financial_data
                  ? <CurrencyCell value={co.latest_ebitda} region={region} />
                  : <Dash />}
              </TD>

              <TD>
                {co.has_financial_data
                  ? <PctCell value={co.latest_ebitda_margin} aboveMedian={co.above_median_ebitda_margin} />
                  : <Dash />}
              </TD>

              <TD>
                {co.has_financial_data
                  ? <CurrencyCell value={co.latest_net_profit} region={region} />
                  : <Dash />}
              </TD>

              <TD>
                {co.has_financial_data
                  ? <PctCell value={co.latest_roic} aboveMedian={co.above_median_roic} />
                  : <Dash />}
              </TD>

              <TD>
                {co.has_financial_data
                  ? <MultipleCell value={co.latest_ev_ebitda} aboveMedian={co.above_median_ev_ebitda} />
                  : <Dash />}
              </TD>

              <TD>
                {co.has_financial_data
                  ? <CurrencyCell value={co.latest_ev} region={region} />
                  : <Dash />}
              </TD>
            </tr>
          ))}

          {sorted.length === 0 && (
            <tr>
              <td colSpan={9} className="px-5 py-8 text-center text-sm text-neutral-400">
                No competitor data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
